import MailOutline from "@mui/icons-material/MailOutline";
import MapOutlined from "@mui/icons-material/MapOutlined";
import PlaceOutlined from "@mui/icons-material/PlaceOutlined";
import type { CustomLocation } from "@/interfaces/redemption";
import { describeCustomLocation } from "@/utils/wellTold";

interface RedemptionReviewCardProps {
  email: string;
  /** Formatted shipping-address lines (see buildReviewAddressLines). */
  addressLines: string[];
  /** Physical gifts show the shipping row; charity/experience show email only. */
  needsShipping: boolean;
  /** Well Told map products: the chosen custom location (a row of its own). */
  customLocation?: CustomLocation | null;
  onEditEmail: () => void;
  onEditAddress: () => void;
  /** Absent ⇒ the location row has no Edit affordance. */
  onEditLocation?: () => void;
}

const ROW_LABEL =
  "inline-flex items-center gap-2 font-lato text-[11px] font-bold uppercase tracking-[1.4px] text-dust-ink";
const EDIT_BTN =
  "-m-1.5 shrink-0 cursor-pointer p-1.5 max-sm:-m-3 max-sm:p-3 font-lato text-[13px] font-semibold text-dust-ink underline underline-offset-[3px]";
const VALUE = "font-lato text-[15px] font-semibold leading-normal text-rodeo-black";

/**
 * Confirmation-review card (RFW-7): a labelled row per detail — confirmation email
 * (always) + shipping address (physical only) + the custom map location (Well Told
 * products only) — each with an Edit affordance.
 */
export default function RedemptionReviewCard({
  email,
  addressLines,
  needsShipping,
  customLocation = null,
  onEditEmail,
  onEditAddress,
  onEditLocation,
}: RedemptionReviewCardProps) {
  const location = customLocation ? describeCustomLocation(customLocation) : null;
  return (
    <div className="w-full overflow-hidden rounded-xl border border-card-border bg-white">
      <div className="px-4.5 py-4">
        <div className="mb-2.25 flex items-center justify-between gap-3">
          <span className={ROW_LABEL}>
            <MailOutline className="text-dark-dust" sx={{ fontSize: 17 }} />
            Confirmation email
          </span>
          <button
            type="button"
            aria-label="Edit confirmation email"
            onClick={onEditEmail}
            className={EDIT_BTN}
          >
            Edit
          </button>
        </div>
        <div className={VALUE}>{email}</div>
      </div>
      {needsShipping && (
        <div className="border-card-border border-t px-4.5 py-4">
          <div className="mb-2.25 flex items-center justify-between gap-3">
            <span className={ROW_LABEL}>
              <PlaceOutlined className="text-dark-dust" sx={{ fontSize: 17 }} />
              Shipping to
            </span>
            <button
              type="button"
              aria-label="Edit shipping address"
              onClick={onEditAddress}
              className={EDIT_BTN}
            >
              Edit
            </button>
          </div>
          <div className={VALUE}>
            {addressLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </div>
      )}
      {location && (
        <div
          className="border-card-border border-t px-4.5 py-4"
          data-testid="review-custom-location"
        >
          <div className="mb-2.25 flex items-center justify-between gap-3">
            <span className={ROW_LABEL}>
              <MapOutlined className="text-dark-dust" sx={{ fontSize: 17 }} />
              {location.label}
            </span>
            {onEditLocation && (
              <button
                type="button"
                aria-label={`Edit ${location.label.toLowerCase()}`}
                onClick={onEditLocation}
                className={EDIT_BTN}
              >
                Edit
              </button>
            )}
          </div>
          <div className={VALUE}>{location.value}</div>
        </div>
      )}
    </div>
  );
}
